import { useState, useEffect, ReactNode, SyntheticEvent } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import BarChartIcon from '@mui/icons-material/BarChart';
import { AdminBusinessDetail } from '../../../types/admin';
import BusinessOverviewTab from './BusinessOverviewTab';
import BusinessAgentsTab from './BusinessAgentsTab';
import BusinessClientsTab from './BusinessClientsTab';
import BusinessAnalyticsTab from './BusinessAnalyticsTab';

interface BusinessDetailModalProps {
  open: boolean;
  businessId: string | null;
  getBusinessDetail: (businessId: string) => AdminBusinessDetail | null;
  onClose: () => void;
  onActivateBusiness?: () => void;
  onDeactivateBusiness?: () => void;
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`business-tabpanel-${index}`}
      aria-labelledby={`business-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

/**
 * BusinessDetailModal - Drawer modal for business details
 *
 * Features:
 * - Slide-in drawer from right
 * - Tabbed interface (Overview, Agents, Clients, Analytics)
 * - Loading and error states
 * - Close button
 * - Business-specific actions
 *
 * Tabs:
 * 1. Overview - Business info, owner, key metrics
 * 2. Agents - All agents with performance data
 * 3. Clients - All clients across agents
 * 4. Analytics - Charts and performance metrics
 */
export default function BusinessDetailModal({
  open,
  businessId,
  getBusinessDetail,
  onClose,
}: BusinessDetailModalProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [business, setBusiness] = useState<AdminBusinessDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && businessId) {
      setIsLoading(true);
      setError(null);

      // Simulate API call delay
      setTimeout(() => {
        const businessDetail = getBusinessDetail(businessId);
        if (businessDetail) {
          setBusiness(businessDetail);
          setIsLoading(false);
        } else {
          setError('Business not found');
          setIsLoading(false);
        }
      }, 300);
    }

    // Reset tab when closed
    if (!open) {
      setActiveTab(0);
      setBusiness(null);
      setError(null);
    }
  }, [open, businessId, getBusinessDetail]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '90%', md: '80%', lg: '70%', xl: '60%' },
          maxWidth: 1400,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {business ? business.name : 'Business Details'}
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Content */}
      {business && !isLoading && !error && (
        <Box>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab
                icon={<InfoIcon />}
                iconPosition="start"
                label="Overview"
                id="business-tab-0"
                aria-controls="business-tabpanel-0"
              />
              <Tab
                icon={<PeopleIcon />}
                iconPosition="start"
                label={`Agents (${business.agents_count})`}
                id="business-tab-1"
                aria-controls="business-tabpanel-1"
              />
              <Tab
                icon={<GroupsIcon />}
                iconPosition="start"
                label={`Clients (${business.clients_count})`}
                id="business-tab-2"
                aria-controls="business-tabpanel-2"
              />
              <Tab
                icon={<BarChartIcon />}
                iconPosition="start"
                label="Analytics"
                id="business-tab-3"
                aria-controls="business-tabpanel-3"
              />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            <BusinessOverviewTab business={business} />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <BusinessAgentsTab business={business} />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <BusinessClientsTab business={business} />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <BusinessAnalyticsTab business={business} />
          </TabPanel>
        </Box>
      )}
    </Drawer>
  );
}
